import { selectDisableSystemCheck } from "modules/systemCheck/redux/selectors";
import { useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { PrecheckStatus } from "./constants";
import { useLobbyCheck } from "./lobby";
import { useOnboardingCheck } from "./onboarding";
import { useSystemCheck } from "./systemCheck";
import { useTrialExpiredCheck } from "./trialExpired";
import { PrecheckResult } from "./types";

type PrechecksResult =
  | { status: PrecheckStatus.PENDING }
  | {
      status: PrecheckStatus.PASS;
    }
  | PrecheckResult;

export const useEventPreChecks = (): PrechecksResult => {
  const trialExpiredCheck = useTrialExpiredCheck();
  const systemCheck = useSystemCheck();
  const lobbyCheck = useLobbyCheck();
  const onboardingCheck = useOnboardingCheck();
  const disableSystemCheck = useSelector(selectDisableSystemCheck);
  const prechecksPassed = useRef(false);

  const prechecksResult = useMemo(() => {
    if (prechecksPassed.current) {
      return { status: PrecheckStatus.PASS as const };
    }

    const checks = [trialExpiredCheck, onboardingCheck];

    if (!disableSystemCheck) {
      checks.push(systemCheck);
    }

    checks.push(lobbyCheck);

    const firstFailedCheck = checks.find(
      (check) => check.status === PrecheckStatus.FAIL,
    );

    if (
      checks.some((check) => check.status === (PrecheckStatus.PENDING as const))
    ) {
      return { status: PrecheckStatus.PENDING as const };
    }

    if (firstFailedCheck) {
      return firstFailedCheck;
    }

    prechecksPassed.current = true;

    return { status: PrecheckStatus.PASS as const };
  }, [
    trialExpiredCheck,
    systemCheck,
    lobbyCheck,
    onboardingCheck,
    disableSystemCheck,
  ]);

  return prechecksResult;
};
